import type {
  LoomAirfoil,
  LoomAuthoringDocument,
  LoomAxis,
  LoomPrimitiveDefinition,
  LoomProfile2d,
  LoomSceneNode,
  LoomVec3,
} from "./loomAuthoringModel";

export interface LoomAabb {
  min: LoomVec3;
  max: LoomVec3;
}

export interface LoomCompiledImplicitField {
  readonly bounds: LoomAabb;
  readonly sample: (x: number, y: number, z: number) => number;
}

interface CompiledNode extends LoomCompiledImplicitField {}
type Vec2 = readonly [number, number];

const vec3 = (x: number, y: number, z: number): LoomVec3 => [x, y, z];
const clamp01 = (value: number): number => Math.max(0, Math.min(1, value));
const mix = (a: number, b: number, t: number): number => a + (b - a) * t;
const radians = (degrees: number): number => degrees * Math.PI / 180;

function unionBounds(items: readonly LoomAabb[]): LoomAabb {
  if (!items.length) throw new Error("implicit union needs at least one bounded input");
  return {
    min: vec3(...([0, 1, 2].map((axis) => Math.min(...items.map((item) => item.min[axis]!))) as [number, number, number])),
    max: vec3(...([0, 1, 2].map((axis) => Math.max(...items.map((item) => item.max[axis]!))) as [number, number, number])),
  };
}

function intersectionBounds(items: readonly LoomAabb[]): LoomAabb {
  const result = {
    min: vec3(...([0, 1, 2].map((axis) => Math.max(...items.map((item) => item.min[axis]!))) as [number, number, number])),
    max: vec3(...([0, 1, 2].map((axis) => Math.min(...items.map((item) => item.max[axis]!))) as [number, number, number])),
  };
  if ([0, 1, 2].some((axis) => result.min[axis]! > result.max[axis]!)) {
    throw new Error("implicit intersection has disjoint conservative bounds");
  }
  return result;
}

function expandBounds(bounds: LoomAabb, amount: number): LoomAabb {
  return {
    min: vec3(bounds.min[0] - amount, bounds.min[1] - amount, bounds.min[2] - amount),
    max: vec3(bounds.max[0] + amount, bounds.max[1] + amount, bounds.max[2] + amount),
  };
}

function profileBounds(profile: LoomProfile2d): { min: Vec2; max: Vec2 } {
  if (profile.kind === "circle") return { min: [-profile.radiusM, -profile.radiusM], max: [profile.radiusM, profile.radiusM] };
  if (profile.kind === "rounded-box") return { min: [-profile.halfExtentsM[0], -profile.halfExtentsM[1]], max: profile.halfExtentsM };
  if (profile.kind === "ellipse" || profile.kind === "superellipse") return { min: [-profile.semiAxesM[0], -profile.semiAxesM[1]], max: profile.semiAxesM };
  return {
    min: [Math.min(...profile.verticesM.map((point) => point[0])), Math.min(...profile.verticesM.map((point) => point[1]))],
    max: [Math.max(...profile.verticesM.map((point) => point[0])), Math.max(...profile.verticesM.map((point) => point[1]))],
  };
}

function polygonField(vertices: readonly Vec2[], x: number, y: number): number {
  let minimumSquared = Number.POSITIVE_INFINITY;
  let inside = false;
  for (let index = 0; index < vertices.length; index += 1) {
    const a = vertices[index]!;
    const b = vertices[(index + 1) % vertices.length]!;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const denominator = dx * dx + dy * dy;
    const t = denominator > 0 ? clamp01(((x - a[0]) * dx + (y - a[1]) * dy) / denominator) : 0;
    const qx = x - (a[0] + t * dx);
    const qy = y - (a[1] + t * dy);
    minimumSquared = Math.min(minimumSquared, qx * qx + qy * qy);
    if ((a[1] > y) !== (b[1] > y) && x < (b[0] - a[0]) * (y - a[1]) / (b[1] - a[1]) + a[0]) inside = !inside;
  }
  return Math.sqrt(minimumSquared) * (inside ? -1 : 1);
}

function profileField(profile: LoomProfile2d, x: number, y: number): number {
  if (profile.kind === "circle") return Math.hypot(x, y) - profile.radiusM;
  if (profile.kind === "rounded-box") {
    const innerX = Math.max(0, profile.halfExtentsM[0] - profile.cornerRadiusM);
    const innerY = Math.max(0, profile.halfExtentsM[1] - profile.cornerRadiusM);
    const qx = Math.abs(x) - innerX;
    const qy = Math.abs(y) - innerY;
    return Math.hypot(Math.max(qx, 0), Math.max(qy, 0)) + Math.min(Math.max(qx, qy), 0) - profile.cornerRadiusM;
  }
  if (profile.kind === "ellipse") {
    return (Math.hypot(x / profile.semiAxesM[0], y / profile.semiAxesM[1]) - 1) * Math.min(...profile.semiAxesM);
  }
  if (profile.kind === "superellipse") {
    const normalized = Math.pow(Math.abs(x / profile.semiAxesM[0]), profile.widthExponent)
      + Math.pow(Math.abs(y / profile.semiAxesM[1]), profile.heightExponent) - 1;
    return normalized * Math.min(...profile.semiAxesM);
  }
  return polygonField(profile.verticesM, x, y);
}

function binomial(n: number, k: number): number {
  let value = 1;
  for (let index = 1; index <= k; index += 1) value = value * (n - index + 1) / index;
  return value;
}

function bernstein(coefficients: readonly number[], u: number): number {
  const order = coefficients.length - 1;
  return coefficients.reduce((sum, coefficient, index) => sum
    + coefficient * binomial(order, index) * Math.pow(u, index) * Math.pow(1 - u, order - index), 0);
}

function airfoilField(airfoil: LoomAirfoil, u: number, v: number): number {
  if (airfoil.family === "polygon") return polygonField(airfoil.vertices, u, v);
  const chordGate = Math.max(-u, u - 1);
  if (airfoil.family === "cst") {
    const bounded = clamp01(u);
    const classFunction = Math.sqrt(bounded) * (1 - bounded);
    const first = classFunction * bernstein(airfoil.upperCoefficients, bounded);
    const second = classFunction * bernstein(airfoil.lowerCoefficients, bounded);
    return Math.max(chordGate, v - Math.max(first, second), Math.min(first, second) - v);
  }
  const bounded = clamp01(u);
  const coefficient = airfoil.closedTrailingEdge ? -0.1036 : -0.1015;
  const thickness = 5 * airfoil.thicknessRatio * (
    0.2969 * Math.sqrt(bounded) - 0.126 * bounded - 0.3516 * bounded ** 2
    + 0.2843 * bounded ** 3 + coefficient * bounded ** 4
  ) + 0.5 * airfoil.trailingEdgeThicknessRatio * bounded;
  const location = airfoil.camberLocationRatio;
  const camber = airfoil.camberRatio === 0 || location === 0
    ? 0
    : bounded < location
      ? airfoil.camberRatio / (location * location) * (2 * location * bounded - bounded * bounded)
      : airfoil.camberRatio / ((1 - location) ** 2) * (1 - 2 * location + 2 * location * bounded - bounded * bounded);
  return Math.max(chordGate, Math.abs(v - camber) - Math.max(0, thickness));
}

function primitiveBounds(definition: LoomPrimitiveDefinition): LoomAabb {
  if (definition.primitive === "sphere") return { min: vec3(-definition.radiusM, -definition.radiusM, -definition.radiusM), max: vec3(definition.radiusM, definition.radiusM, definition.radiusM) };
  if (definition.primitive === "box") return { min: vec3(...definition.dimensionsM.map((value) => -value / 2) as [number, number, number]), max: vec3(...definition.dimensionsM.map((value) => value / 2) as [number, number, number]) };
  if (definition.primitive === "ellipsoid") return { min: vec3(...definition.radiiM.map((value) => -value) as [number, number, number]), max: definition.radiiM };
  if (definition.primitive === "pod") {
    const radius = definition.lengthM / (2 * definition.finenessRatio);
    return { min: vec3(definition.noseXM, -radius, -radius), max: vec3(definition.noseXM + definition.lengthM, radius, radius) };
  }
  if (definition.primitive === "extrusion") {
    const profile = profileBounds(definition.profile);
    if (definition.plane === "xy") return { min: vec3(profile.min[0], profile.min[1], -definition.halfLengthM), max: vec3(profile.max[0], profile.max[1], definition.halfLengthM) };
    if (definition.plane === "xz") return { min: vec3(profile.min[0], -definition.halfLengthM, profile.min[1]), max: vec3(profile.max[0], definition.halfLengthM, profile.max[1]) };
    return { min: vec3(-definition.halfLengthM, profile.min[0], profile.min[1]), max: vec3(definition.halfLengthM, profile.max[0], profile.max[1]) };
  }
  if (definition.primitive === "revolution") {
    const profile = profileBounds(definition.profile);
    const radius = Math.max(Math.abs(definition.radialOffsetM + profile.min[1]), Math.abs(definition.radialOffsetM + profile.max[1]));
    if (definition.axis === "x") return { min: vec3(profile.min[0], -radius, -radius), max: vec3(profile.max[0], radius, radius) };
    if (definition.axis === "y") return { min: vec3(-radius, profile.min[0], -radius), max: vec3(radius, profile.max[0], radius) };
    return { min: vec3(-radius, -radius, profile.min[0]), max: vec3(radius, radius, profile.max[0]) };
  }
  if (definition.primitive === "fuselage") {
    const stations = definition.stations;
    return {
      min: vec3(Math.min(...stations.map((station) => station.xM)), Math.min(...stations.map((station) => station.kind === "point" ? 0 : -station.widthM / 2)), Math.min(...stations.map((station) => station.kind === "point" ? station.zCenterM : station.zCenterM - station.heightM / 2))),
      max: vec3(Math.max(...stations.map((station) => station.xM)), Math.max(...stations.map((station) => station.kind === "point" ? 0 : station.widthM / 2)), Math.max(...stations.map((station) => station.kind === "point" ? station.zCenterM : station.zCenterM + station.heightM / 2))),
    };
  }
  const boxes = definition.panels.map((panel) => {
    const span = panel.yTipM - panel.yRootM;
    const sweepLineRoot = panel.xOffsetM + panel.sweepLocationRatio * panel.chordRootM;
    const tipLeading = sweepLineRoot + Math.tan(radians(panel.sweepDeg)) * span - panel.sweepLocationRatio * panel.chordTipM;
    const rootCenter = panel.xOffsetM + panel.chordRootM / 2;
    const tipCenter = tipLeading + panel.chordTipM / 2;
    const rootZ = panel.zOffsetM;
    const tipZ = rootZ + Math.tan(radians(panel.dihedralDeg)) * span;
    return {
      min: vec3(Math.min(rootCenter - panel.chordRootM, tipCenter - panel.chordTipM), Math.min(panel.yRootM, panel.yTipM), Math.min(rootZ - panel.chordRootM, tipZ - panel.chordTipM)),
      max: vec3(Math.max(rootCenter + panel.chordRootM, tipCenter + panel.chordTipM), Math.max(panel.yRootM, panel.yTipM), Math.max(rootZ + panel.chordRootM, tipZ + panel.chordTipM)),
    };
  });
  return unionBounds(boxes);
}

function primitiveField(definition: LoomPrimitiveDefinition): CompiledNode {
  const bounds = primitiveBounds(definition);
  if (definition.primitive === "sphere") return { bounds, sample: (x, y, z) => Math.hypot(x, y, z) - definition.radiusM };
  if (definition.primitive === "box") return { bounds, sample: (x, y, z) => {
    const q = [Math.abs(x) - definition.dimensionsM[0] / 2, Math.abs(y) - definition.dimensionsM[1] / 2, Math.abs(z) - definition.dimensionsM[2] / 2];
    return Math.hypot(Math.max(q[0]!, 0), Math.max(q[1]!, 0), Math.max(q[2]!, 0)) + Math.min(Math.max(...q), 0);
  } };
  if (definition.primitive === "ellipsoid") return { bounds, sample: (x, y, z) => (Math.hypot(x / definition.radiiM[0], y / definition.radiiM[1], z / definition.radiiM[2]) - 1) * Math.min(...definition.radiiM) };
  if (definition.primitive === "pod") return { bounds, sample: (x, y, z) => {
    const radius = definition.lengthM / (2 * definition.finenessRatio);
    const center = definition.noseXM + definition.lengthM / 2;
    if (definition.profile === "ellipse") return (Math.hypot((x - center) / (definition.lengthM / 2), y / radius, z / radius) - 1) * radius;
    const start = definition.noseXM + radius;
    const end = definition.noseXM + definition.lengthM - radius;
    return Math.hypot(x - Math.max(start, Math.min(end, x)), y, z) - radius;
  } };
  if (definition.primitive === "extrusion") return { bounds, sample: (x, y, z) => {
    const profile = definition.plane === "xy" ? profileField(definition.profile, x, y) : definition.plane === "xz" ? profileField(definition.profile, x, z) : profileField(definition.profile, y, z);
    const axial = definition.plane === "xy" ? z : definition.plane === "xz" ? y : x;
    return Math.max(profile, Math.abs(axial) - definition.halfLengthM);
  } };
  if (definition.primitive === "revolution") return { bounds, sample: (x, y, z) => {
    const axial = definition.axis === "x" ? x : definition.axis === "y" ? y : z;
    const radial = definition.axis === "x" ? Math.hypot(y, z) : definition.axis === "y" ? Math.hypot(x, z) : Math.hypot(x, y);
    return profileField(definition.profile, axial, radial - definition.radialOffsetM);
  } };
  if (definition.primitive === "fuselage") return fuselageField(definition, bounds);
  return wingField(definition, bounds);
}

function fuselageField(definition: Extract<LoomPrimitiveDefinition, { primitive: "fuselage" }>, bounds: LoomAabb): CompiledNode {
  const stations = [...definition.stations].sort((a, b) => a.xM - b.xM);
  return { bounds, sample: (x, y, z) => {
    let lower = stations[0]!;
    let upper = stations[stations.length - 1]!;
    for (let index = 0; index < stations.length - 1; index += 1) {
      if (x >= stations[index]!.xM && x <= stations[index + 1]!.xM) { lower = stations[index]!; upper = stations[index + 1]!; break; }
    }
    const t = clamp01((x - lower.xM) / Math.max(Number.EPSILON, upper.xM - lower.xM));
    const width = mix(lower.kind === "point" ? 0 : lower.widthM, upper.kind === "point" ? 0 : upper.widthM, t) / 2;
    const height = mix(lower.kind === "point" ? 0 : lower.heightM, upper.kind === "point" ? 0 : upper.heightM, t) / 2;
    const centerZ = mix(lower.zCenterM, upper.zCenterM, t);
    const widthExponent = mix(lower.kind === "point" ? 2 : lower.widthExponent, upper.kind === "point" ? 2 : upper.widthExponent, t);
    const heightExponent = mix(lower.kind === "point" ? 2 : lower.heightExponent, upper.kind === "point" ? 2 : upper.heightExponent, t);
    const radial = width <= 1e-12 || height <= 1e-12 ? Math.hypot(y, z - centerZ) : (
      Math.pow(Math.abs(y / width), widthExponent) + Math.pow(Math.abs((z - centerZ) / height), heightExponent) - 1
    ) * Math.min(width, height);
    return Math.max(radial, bounds.min[0] - x, x - bounds.max[0]);
  } };
}

function wingField(definition: Extract<LoomPrimitiveDefinition, { primitive: "wing" }>, bounds: LoomAabb): CompiledNode {
  const panels = definition.panels.map((panel, panelIndex) => ({ panel, panelIndex }));
  return { bounds, sample: (x, y, z) => Math.min(...panels.map(({ panel, panelIndex }) => {
    const span = panel.yTipM - panel.yRootM;
    const t = clamp01((y - panel.yRootM) / span);
    const chord = mix(panel.chordRootM, panel.chordTipM, t);
    const sweepLineRoot = panel.xOffsetM + panel.sweepLocationRatio * panel.chordRootM;
    const leading = sweepLineRoot + Math.tan(radians(panel.sweepDeg)) * span * t - panel.sweepLocationRatio * chord;
    const centerZ = panel.zOffsetM + Math.tan(radians(panel.dihedralDeg)) * span * t;
    const twist = radians(mix(panel.twistRootDeg, panel.twistTipDeg, t));
    const originX = leading + panel.twistLocationRatio * chord;
    const dx = x - originX;
    const dz = z - centerZ;
    const untwistedX = Math.cos(twist) * dx - Math.sin(twist) * dz + panel.twistLocationRatio * chord;
    const untwistedZ = Math.sin(twist) * dx + Math.cos(twist) * dz;
    const airfoil = mixAirfoilField(panel.airfoilRoot, panel.airfoilTip, t, untwistedX / chord, untwistedZ / chord) * chord;
    const spanGate = Math.max(Math.min(panel.yRootM, panel.yTipM) - y, y - Math.max(panel.yRootM, panel.yTipM));
    const isTipPanel = panelIndex === panels.length - 1 && definition.tip === "round" && definition.tipRadiusM > 0;
    return isTipPanel ? smoothMaximum(airfoil, spanGate, definition.tipRadiusM) : Math.max(airfoil, spanGate);
  })) };
}

function mixAirfoilField(root: LoomAirfoil, tip: LoomAirfoil, t: number, u: number, v: number): number {
  if (root.family !== tip.family) return Math.max(airfoilField(root, u, v), airfoilField(tip, u, v));
  return mix(airfoilField(root, u, v), airfoilField(tip, u, v), t);
}

function smoothMinimum(a: number, b: number, radius: number): number {
  if (radius <= 0) return Math.min(a, b);
  const h = clamp01(0.5 + 0.5 * (b - a) / radius);
  return mix(b, a, h) - radius * h * (1 - h);
}

function smoothMaximum(a: number, b: number, radius: number): number {
  return -smoothMinimum(-a, -b, radius);
}

function rotatePoint(point: LoomVec3, axis: LoomAxis, angle: number, origin: LoomVec3 = [0, 0, 0]): LoomVec3 {
  const x = point[0] - origin[0]; const y = point[1] - origin[1]; const z = point[2] - origin[2];
  const c = Math.cos(angle); const s = Math.sin(angle);
  if (axis === "x") return vec3(x + origin[0], c * y - s * z + origin[1], s * y + c * z + origin[2]);
  if (axis === "y") return vec3(c * x + s * z + origin[0], y + origin[1], -s * x + c * z + origin[2]);
  return vec3(c * x - s * y + origin[0], s * x + c * y + origin[1], z + origin[2]);
}

function rotateBounds(bounds: LoomAabb, axis: LoomAxis, angle: number, origin: LoomVec3): LoomAabb {
  const corners: LoomVec3[] = [];
  for (const x of [bounds.min[0], bounds.max[0]]) for (const y of [bounds.min[1], bounds.max[1]]) for (const z of [bounds.min[2], bounds.max[2]]) corners.push(rotatePoint([x, y, z], axis, angle, origin));
  return unionBounds(corners.map((corner) => ({ min: corner, max: corner })));
}

function transformNode(node: Extract<LoomSceneNode, { type: "transform" }>, input: CompiledNode): CompiledNode {
  const operation = node.operation;
  if (operation.transform === "translate") return {
    bounds: { min: vec3(...input.bounds.min.map((value, axis) => value + operation.offsetM[axis]!) as [number, number, number]), max: vec3(...input.bounds.max.map((value, axis) => value + operation.offsetM[axis]!) as [number, number, number]) },
    sample: (x, y, z) => input.sample(x - operation.offsetM[0], y - operation.offsetM[1], z - operation.offsetM[2]),
  };
  if (operation.transform === "uniform-scale") return {
    bounds: { min: vec3(...input.bounds.min.map((value) => value * operation.factor) as [number, number, number]), max: vec3(...input.bounds.max.map((value) => value * operation.factor) as [number, number, number]) },
    sample: (x, y, z) => input.sample(x / operation.factor, y / operation.factor, z / operation.factor) * operation.factor,
  };
  const axis = operation.transform === "mirror-y" ? "y" : operation.axis;
  if (operation.transform === "mirror" || operation.transform === "mirror-y") {
    const offset = operation.transform === "mirror" ? operation.planeOffsetM : 0;
    const coordinate = axis === "x" ? 0 : axis === "y" ? 1 : 2;
    const reflectedMin = 2 * offset - input.bounds.max[coordinate]!;
    const reflectedMax = 2 * offset - input.bounds.min[coordinate]!;
    const min = [...input.bounds.min] as [number, number, number]; const max = [...input.bounds.max] as [number, number, number];
    min[coordinate] = reflectedMin; max[coordinate] = reflectedMax;
    return { bounds: { min, max }, sample: (x, y, z) => {
      const point: [number, number, number] = [x, y, z]; point[coordinate] = 2 * offset - point[coordinate];
      return input.sample(...point);
    } };
  }
  const angle = radians(operation.angleDeg);
  return {
    bounds: rotateBounds(input.bounds, operation.axis, angle, operation.originM),
    sample: (x, y, z) => input.sample(...rotatePoint([x, y, z], operation.axis, -angle, operation.originM)),
  };
}

export function compileLoomImplicitField(document: LoomAuthoringDocument): LoomCompiledImplicitField {
  const nodes = new Map(document.nodes.map((node) => [node.id, node]));
  const cache = new Map<string, CompiledNode>();
  const visiting = new Set<string>();
  const compile = (id: string): CompiledNode => {
    const cached = cache.get(id); if (cached) return cached;
    if (visiting.has(id)) throw new Error(`implicit graph contains a cycle at ${id}`);
    const node = nodes.get(id); if (!node) throw new Error(`implicit graph references missing node ${id}`);
    visiting.add(id);
    let result: CompiledNode;
    if (node.type === "import") throw new Error(`OpenVSP import node ${node.id} has no native scalar field; convert it before implicit extraction`);
    if (node.type === "primitive") result = primitiveField(node.definition);
    else if (node.type === "transform") result = transformNode(node, compile(node.inputNodeId));
    else {
      const inputs = node.inputNodeIds.map(compile);
      if (node.operation === "intersection") result = { bounds: intersectionBounds(inputs.map((item) => item.bounds)), sample: (x, y, z) => Math.max(...inputs.map((item) => item.sample(x, y, z))) };
      else if (node.operation === "subtraction") result = { bounds: inputs[0]!.bounds, sample: (x, y, z) => Math.max(inputs[0]!.sample(x, y, z), -inputs[1]!.sample(x, y, z)) };
      else if (node.operation === "smooth-union") {
        const radius = node.blendRadiusM ?? 0;
        result = { bounds: expandBounds(unionBounds(inputs.map((item) => item.bounds)), radius), sample: (x, y, z) => smoothMinimum(inputs[0]!.sample(x, y, z), inputs[1]!.sample(x, y, z), radius) };
      } else result = { bounds: unionBounds(inputs.map((item) => item.bounds)), sample: (x, y, z) => Math.min(...inputs.map((item) => item.sample(x, y, z))) };
    }
    visiting.delete(id); cache.set(id, result); return result;
  };
  if (!document.rootNodeIds.length) throw new Error("implicit extraction needs at least one root node");
  const roots = document.rootNodeIds.map(compile);
  return roots.length === 1 ? roots[0]! : { bounds: unionBounds(roots.map((root) => root.bounds)), sample: (x, y, z) => Math.min(...roots.map((root) => root.sample(x, y, z))) };
}
